import React, { useState, useRef, useEffect } from "react";
import { Container, Nav, NavItem, NavLink } from "reactstrap";
import classnames from "classnames";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useParams,
} from "react-router-dom";
import studyTabsStyles from "../../AssigningStudyModule/StudyScreeningTabs/css/studytabs.module.scss";
import VolunteerTests from "modules/Volunteer/VolunteerTest/pages/VolunteerTestsList";
import { useVolunteerService } from "modules/Volunteer/MasterVolunteer/api/useVolunteerQuery";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import ViewVolunteerDetails from "modules/Volunteer/MasterVolunteer/pages/ViewVolunteerDetails";
import StudyICF from "../../AssigningStudyModule/StudyICF/pages/StudyICF";
import { VolunteerTabType } from "../helper/volunteer.types";
import { volunteerTabs } from "../helper/volunteer.constant";
import { STUDY_VOLUNTEER_PATHS } from "Routes/paths";

const VolunteerStudyInfoTab: React.FC = () => {

  document.title = "Volunteer Study Info | ZENTIXS VISTA";
  
  const [activeTab, setActiveTab] = useState<VolunteerTabType>("detail");
  const [isOpen, setIsOpen] = useState(false);
  const segmentTabsRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const [slider, setSlider] = useState({
    left: "2px",
    width: "80px",
  });

  const navigate = useNavigate();
  const location = useLocation();
  const VolunteerName = sessionStorage.getItem("volunteerName") || "";
  const StudyName = sessionStorage.getItem("studyName") || "";
  const combinedTitle = `${VolunteerName} - ${StudyName}`;
  /* ---------- Volunteer API ---------- */

  const { id } = useParams();

  const { SelectQuery } = useVolunteerService({
    id: Number(id),
  });

  const volunteerData = SelectQuery?.data?.data?.[0];
  const isLoading = SelectQuery?.isLoading || SelectQuery?.isFetching;

  /* ---------- Sync tab with URL ---------- */

  useEffect(() => {
    const last = location.pathname.split("/").filter(Boolean).pop();

    if (last === "test") {
      setActiveTab("test");
    } else if (last === "icf") {
      setActiveTab("icf");
    } else {
      setActiveTab("detail");
    }
  }, [location.pathname]);

  /* ---------- Tab click ---------- */

  const toggleTab = (tabKey: VolunteerTabType, index: number) => {
    navigate(tabKey === "detail" ? "" : tabKey);

    setActiveTab(tabKey);

    const el = tabRefs.current[index];

    const padding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + padding + "px",
        width: el.offsetWidth + "px",
      });
    }
  };

  /* ---------- Slider reposition ---------- */

  useEffect(() => {
    const index = volunteerTabs.findIndex((t) => t.key === activeTab);
    const el = tabRefs.current[index];

    const padding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + padding + "px",
        width: el.offsetWidth + "px",
      });
    }
  }, [activeTab]);

  /* ---------- Render ---------- */

  return (
    <Container fluid>
      <div className={studyTabsStyles.study_tab_wrapper}>
        <CommonTabHeader
          title={combinedTitle}
          isOpen={isOpen}
          toggle={() => setIsOpen(!isOpen)}
          activeTab={activeTab}
        />
        <div
          className={`d-flex justify-content-between align-items-center bg-white ${studyTabsStyles.study_tab_container} mb-3`}
        >
          <Nav
            innerRef={segmentTabsRef}
            className={studyTabsStyles.segmentTabs}
          >
            <div
              className={studyTabsStyles.slider}
              style={
                {
                  "--left": slider.left,
                  "--width": slider.width,
                } as React.CSSProperties
              }
            />

            {volunteerTabs.map((t, index) => (
              <NavItem key={t.key}>
                <NavLink
                  innerRef={(el) => {
                    tabRefs.current[index] = el;
                  }}
                  className={classnames(studyTabsStyles.tab, {
                    [studyTabsStyles.active]: activeTab === t.key,
                  })}
                  onClick={() => toggleTab(t.key, index)}
                  style={{ cursor: "pointer" }}
                >
                  <i className={`${t.icon} me-1`} />
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </div>

        {/* ---------- Routes ---------- */}

        <div>
          <Routes>
            <Route
              index
              element={
                <ViewVolunteerDetails
                  key={location.pathname}
                  volunteerData={volunteerData}
                  isLoading={isLoading}
                  breadcrumbItems={[
                    { text: "Studies", link: STUDY_VOLUNTEER_PATHS.DETAILS },
                    { text: "Volunteer Study Info" },
                  ]}
                />
              }
            />
            <Route path="icf" element={<StudyICF />} />
            <Route path="test" element={<VolunteerTests />} />

            <Route
              path="*"
              element={
                <ViewVolunteerDetails
                  key={location.pathname}
                  volunteerData={volunteerData}
                  isLoading={isLoading}
                  breadcrumbItems={[
                    { text: "Studies", link: STUDY_VOLUNTEER_PATHS.DETAILS },
                    { text: "Volunteer Study Info" },
                  ]}
                />
              }
            />
          </Routes>
        </div>
      </div>
    </Container>
  );
};

export default VolunteerStudyInfoTab;
